import type { ComponentType } from 'react'
import {
  AboutPage,
  BiosPage,
  CaseStudiesPage,
  ContactPage,
  DetailPage,
  HomePage,
  PlatformPage,
  ResourcesPage,
  SecurityTrustPage,
  SolutionsPage,
  type PageProps,
} from './pages'

export interface RouteConfig {
  path: string
  label: string
  component: ComponentType<PageProps>
}

export interface NavItem {
  label: string
  path: string
  description?: string
  children?: NavItem[]
}

const governancePage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="Governance & OCAP"
    title="Governance systems that make OCAP operational."
    description="Awareness is not implementation. We help organizations build the policies, workflows, and stewardship structures that make OCAP-aligned governance hold in daily practice."
    problemTitle="Principles alone do not protect data."
    problemCopy="Many organizations understand the importance of OCAP but still rely on fragmented tools, informal processes, or external platforms that weaken control. The gap is not intent—it is implementation."
    whyItMatters={[
      'OCAP language becomes operational rather than symbolic.',
      'Leadership gains clearer authority over stewardship and access decisions.',
      'Teams get workflows that support governance instead of bypassing it.',
      'Policy, systems, and internal practice become more aligned.',
    ]}
    bullets={[
      'Readiness and gap assessment',
      'Data governance framework',
      'Policy and protocol development',
      'Data classification and stewardship model',
      'Consent, access, and use workflow design',
      'Staff enablement and implementation support',
    ]}
    outcomes={[
      'Clearer accountability',
      'Stronger stewardship practice',
      'Better alignment between governance and operations',
      'Lower exposure around data handling and access',
    ]}
  />
)

const infrastructurePage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="Infrastructure"
    title="Infrastructure designed for control, resilience, and long-term stewardship."
    description="We help organizations move beyond default vendor environments into digital systems that reflect governance priorities, operational realities, and security requirements."
    problemTitle="Convenience often comes at the cost of control."
    problemCopy="Many teams inherit fragmented cloud tools, unmanaged access, and weak visibility into where data lives or how systems connect. Over time, that creates lock-in, risk, and governance drift."
    whyItMatters={[
      'Infrastructure decisions become easier to evaluate through a governance lens.',
      'Operational risk becomes more visible before it becomes disruptive.',
      'Leadership gets a clearer basis for hosting and access decisions.',
      'The environment becomes easier to sustain and evolve over time.',
    ]}
    bullets={[
      'Current-state environment review',
      'Architecture and residency recommendations',
      'Secure collaboration design',
      'Identity and access structure',
      'Migration and transition planning',
      'Deployment support',
    ]}
    outcomes={[
      'Better visibility into systems and dependencies',
      'Stronger control over access and hosting choices',
      'Reduced vendor concentration risk',
      'A more secure foundation for modernization',
    ]}
  />
)

const aiFutureReadinessPage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="AI & Future Readiness"
    title="Prepare for AI with governance first."
    description="AI decisions affect data use, procurement, policy, and public trust. We help organizations assess readiness and establish the guardrails required for responsible deployment."
    problemTitle="AI pressure is moving faster than governance capacity."
    problemCopy="Teams are being asked to evaluate AI tools before they have clear policies, risk thresholds, or data-use boundaries. That is not innovation—that is unmanaged exposure."
    whyItMatters={[
      'AI adoption decisions become more disciplined and defensible.',
      'Data-use risk is assessed before tools become normalized internally.',
      'Leadership gets a practical basis for saying yes, no, or not yet.',
      'Readiness improves without forcing premature deployment.',
    ]}
    bullets={[
      'AI readiness assessment',
      'Use-case screening',
      'AI governance framework',
      'Risk and impact analysis',
      'Deployment pathway recommendations',
      'Staff and leadership training',
    ]}
    outcomes={[
      'Better decisions about where AI should and should not be used',
      'Lower risk of inappropriate data exposure',
      'Stronger internal governance',
      'A practical roadmap for future capability',
    ]}
  />
)

const riskAdvisoryPage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="Risk & Advisory"
    title="Digital risks hiding inside everyday platforms and vendor relationships."
    description="We review dependencies, data flows, governance gaps, and procurement exposure so leadership can make stronger decisions before risk becomes disruption."
    whyItMatters={[
      'Hidden external dependencies become visible.',
      'Vendor renewals and procurement decisions get stronger support.',
      'Data flow and access exposure are easier to evaluate.',
      'Leadership can act before misalignment becomes operational risk.',
    ]}
    bullets={[
      'Vendor lock-in analysis',
      'Data flow review',
      'Governance and contract misalignment review',
      'Privacy and access exposure mapping',
      'Leadership briefing',
      'Decision-ready recommendations',
    ]}
    outcomes={[
      'Better procurement and renewal decisions',
      'Reduced hidden risk',
      'Clearer view of external dependencies',
      'Stronger negotiating position with vendors',
    ]}
  />
)

const ocapPage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="OCAP"
    title="OCAP requires more than acknowledgement."
    description="Ownership, Control, Access, and Possession should shape digital systems, not sit beside them as separate policy language."
    whyItMatters={[
      'OCAP can be expressed in workflows, controls, and system design.',
      'Leadership gains clearer stewardship visibility.',
      'Access and consent decisions become more deliberate.',
      'Governance principles are reinforced through operations.',
    ]}
    bullets={[
      'Governance-led architecture decisions',
      'Clearer data stewardship roles',
      'Access and consent controls',
      'Better visibility into where systems and data reside',
      'Policy-to-practice implementation support',
    ]}
  />
)

const aiReadinessPage: ComponentType<PageProps> = (props) => (
  <DetailPage
    {...props}
    badge="AI Readiness"
    title="AI readiness is now a governance issue."
    description="Before organizations adopt AI tools, they need policy, oversight, risk thresholds, and a clear understanding of how data may be used or exposed."
    whyItMatters={[
      'AI conversations move from reaction to governance-led assessment.',
      'Policy and oversight gaps become visible earlier.',
      'Leadership can evaluate use cases with clearer risk boundaries.',
      'Future adoption becomes more deliberate and sustainable.',
    ]}
    bullets={[
      'Why AI readiness matters now',
      'What readiness includes',
      'Common organizational risks',
      'Responsible deployment pathway',
      'Training and policy support',
    ]}
  />
)

export const solutionsChildren: NavItem[] = [
  {
    label: 'OCAP Governance Consulting',
    path: '/services/governance-ocap',
    description: 'Turn stewardship principles into practical operating controls.',
  },
  {
    label: 'Infrastructure Advisory',
    path: '/services/infrastructure',
    description: 'Sovereign infrastructure design and implementation support.',
  },
  {
    label: 'AI Readiness & Policy',
    path: '/services/ai-readiness',
    description: 'Governance-first AI preparation and deployment planning.',
  },
  {
    label: 'Vendor Risk Assessment',
    path: '/services/risk-advisory',
    description: 'Review vendors, dependencies, and procurement exposure.',
  },
  {
    label: 'Rez-Hub Platform Implementation',
    path: '/platform',
    description: 'Our flagship sovereign digital platform deployment.',
  },
]

export const routes: RouteConfig[] = [
  { path: '/', label: 'Home', component: HomePage },
  { path: '/services', label: 'Services', component: SolutionsPage },
  { path: '/services/governance-ocap', label: 'OCAP Governance', component: governancePage },
  { path: '/services/infrastructure', label: 'Infrastructure Advisory', component: infrastructurePage },
  {
    path: '/services/ai-readiness',
    label: 'AI Readiness',
    component: aiFutureReadinessPage,
  },
  { path: '/services/risk-advisory', label: 'Vendor Risk Assessment', component: riskAdvisoryPage },
  { path: '/platform', label: 'Rez-Hub Platform', component: PlatformPage },
  { path: '/ocap', label: 'OCAP Expertise', component: ocapPage },
  { path: '/ai-readiness', label: 'AI Readiness', component: aiReadinessPage },
  { path: '/security-trust', label: 'Security & Trust', component: SecurityTrustPage },
  { path: '/about', label: 'About', component: AboutPage },
  { path: '/bios', label: 'Leadership', component: BiosPage },
  { path: '/case-studies', label: 'Case Studies', component: CaseStudiesPage },
  { path: '/resources', label: 'Resources', component: ResourcesPage },
  { path: '/contact', label: 'Contact', component: ContactPage },
]
